import React, { useEffect } from 'react';
// @ts-ignore - TypeScript casing issue
import { useRecoilValue } from 'recoil';
import { SpeakerHigh } from 'phosphor-react';
import { useSpeechSynthesis } from '../../../SpeakingPractice/hooks/useSpeechSynthesis';
// @ts-ignore - TypeScript casing issue
import { speakingAudioRecordingsState } from '../../../../../recoil/atoms';
import OverallScoreCard from '../../../SpeakingPractice/components/OverallScoreCard';
import RecapSection from '../RecapSection';
import ReportSoalButton from '../../../LanguageLearning/ReportSoalButton';
import { ReviewQuestionHeader } from '../shared/ReviewQuestionHeader';
import QuestionAudioPlayer from '../../../shared/QuestionAudioPlayer';

const PronunciationReview = ({ question, userAnswer, isCorrect, explanation = '', recap = '', annotations = [], langCode = 'de', reportProps = null }) => {
  const speech = useSpeechSynthesis(langCode);
  // changed by Cursor: read audio from Recoil instead of localStorage (reliable, no quota issues)
  const audioRecordings = useRecoilValue(speakingAudioRecordingsState);
  
  console.log('🔍 PronunciationReview - Raw Props:', {
    question,
    userAnswer,
    userAnswerType: typeof userAnswer,
    isArray: Array.isArray(userAnswer),
    isObject: typeof userAnswer === 'object' && !Array.isArray(userAnswer),
    isCorrect,
  });
  
  const questionNumber = question.number || question['question-number'] || 1;
  
  // Extract all possible question IDs (from multiple sources)
  // This ensures we can match audio saved during practice
  const possibleQuestionIds = [
    question.originalQuestion?.['soal-id'],
    question.originalQuestion?.id,
    question.originalQuestion?.soalId,
    question['soal-id'],
    question.soalId,
    question.id,
  ].filter(Boolean); // Remove null/undefined
  
  // Primary questionId for assessment lookup (use first available)
  // Note: questionId is used in assessment lookup below, so we keep it even if eslint warns
  // eslint-disable-next-line no-unused-vars
  const questionId = possibleQuestionIds[0] || null;
  
  // Extract assessment result from userAnswer
  // Check if userAnswer is object (not array, not string)
  let assessmentResult = (typeof userAnswer === 'object' && !Array.isArray(userAnswer) && userAnswer?.assessment) 
    ? userAnswer.assessment 
    : null;
  
  // Fallback: Try to get from localStorage if not in props
  // Try all possible questionIds
  if (!assessmentResult && possibleQuestionIds.length > 0) {
    console.log('🔍 PronunciationReview - Searching for assessment in localStorage:', {
      possibleQuestionIds,
      questionNumber
    });
    
    for (const qId of possibleQuestionIds) {
      try {
        const savedAssessmentKey = `pronunciation-assessment-${qId}`;
        const savedAssessment = localStorage.getItem(savedAssessmentKey);
        if (savedAssessment) {
          assessmentResult = JSON.parse(savedAssessment);
          console.log('✅ Loaded assessment from localStorage in PronunciationReview:', qId);
          break;
        }
      } catch (err) {
        console.error('Error parsing assessment for qId:', qId, err);
        // Continue to next ID
      }
    }
    
    // If still not found, try to find any assessment with matching soal-id pattern
    if (!assessmentResult) {
      try {
        const allKeys = Object.keys(localStorage);
        const assessmentKeys = allKeys.filter(key => key.startsWith('pronunciation-assessment-'));
        console.log('🔍 PronunciationReview - Searching all assessment keys:', {
          totalKeys: assessmentKeys.length,
          assessmentKeys: assessmentKeys.map(k => k.replace('pronunciation-assessment-', ''))
        });
        
        // changed by Cursor: gunakan String() conversion untuk hindari type mismatch
        const possibleIdsAsStringForAssessment = possibleQuestionIds.map(String);
        for (const key of assessmentKeys) {
          const keySoalId = key.replace('pronunciation-assessment-', '');
          if (possibleIdsAsStringForAssessment.includes(keySoalId)) {
            const savedAssessment = localStorage.getItem(key);
            if (savedAssessment) {
              assessmentResult = JSON.parse(savedAssessment);
              console.log('✅ Loaded assessment from localStorage by matching key:', key);
              break;
            }
          }
        }
      } catch (err) {
        console.error('Error searching assessment keys:', err);
      }
    }
    
    if (!assessmentResult) {
      console.warn('⚠️ PronunciationReview - No assessment found in localStorage:', {
        possibleQuestionIds,
        questionNumber,
        allAssessmentKeys: Object.keys(localStorage).filter(k => k.startsWith('pronunciation-assessment-'))
      });
    }
  }
  
  // Extract audio URL from userAnswer
  let audioUrl = (typeof userAnswer === 'object' && !Array.isArray(userAnswer) && userAnswer?.audioUrl) 
    ? userAnswer.audioUrl 
    : null;
  
  // changed by Cursor: fallback to Recoil state (replaces localStorage - reliable, no quota issues)
  if (!audioUrl) {
    const possibleIdsAsString = possibleQuestionIds.map(String);
    
    // Strategy 1: Match by soal-id
    for (const [, recording] of Object.entries(audioRecordings)) {
      if (recording?.soalId && possibleIdsAsString.includes(String(recording.soalId))) {
        audioUrl = recording.audioUrl;
        console.log('✅ PronunciationReview - Found audio in Recoil by soal-id:', recording.soalId);
        break;
      }
    }
    
    // Strategy 2: Match by index (questionNumber - 1)
    if (!audioUrl) {
      const taskIndex = (questionNumber - 1).toString();
      if (audioRecordings[taskIndex]?.audioUrl) {
        audioUrl = audioRecordings[taskIndex].audioUrl;
        console.log('✅ PronunciationReview - Found audio in Recoil by index:', taskIndex);
      }
    }
    
    if (!audioUrl) {
      console.warn('⚠️ PronunciationReview - No audio recording found:', { questionNumber, possibleQuestionIds });
    }
  } else {
    console.log('✅ PronunciationReview - Using audioUrl from props');
  }
  
  // Extract text to read from userAnswer object or question
  // Priority: userAnswer.text (from ResultPage) > question fields
  const textToRead = (typeof userAnswer === 'object' && !Array.isArray(userAnswer) && userAnswer?.text)
      ? userAnswer.text
    : (question.text || question['text-soal'] || question.question || question['text-to-read'] || question.originalQuestion?.['text-to-read'] || question.originalQuestion?.text || '');
  
  // Extract translation from question
  const translation = question.translation || question['translation'] || question.originalQuestion?.translation || question.originalQuestion?.['translation'] || '';
  
  const overallScores = assessmentResult?.['overall-scores'] || {};
  const wordDetails = assessmentResult?.['word-details'] || [];
  
  console.log('🔍 PronunciationReview - Extracted:', {
    textToRead,
    questionNumber,
    hasAssessmentResult: !!assessmentResult,
    assessmentResultKeys: assessmentResult ? Object.keys(assessmentResult) : [],
    hasAudio: !!audioUrl,
    audioUrl: audioUrl ? audioUrl.substring(0, 100) : null,
    audioUrlType: audioUrl ? (audioUrl.startsWith('data:') ? 'base64' : audioUrl.startsWith('blob:') ? 'blob' : 'unknown') : null,
    overallScoresKeys: Object.keys(overallScores),
    'pronunciation-score': overallScores['pronunciation-score'],
    wordDetailsCount: wordDetails.length,
    'userAnswer.text': typeof userAnswer === 'object' ? userAnswer?.text : 'not object',
    'userAnswer.audioUrl': typeof userAnswer === 'object' ? (userAnswer?.audioUrl ? userAnswer.audioUrl.substring(0, 50) : 'none') : 'not object',
  });
  
  // CRITICAL: Log final audio state before render
  useEffect(() => {
    console.log('🎯 PronunciationReview - Final audio state before render:', {
      hasAudioUrl: !!audioUrl,
      audioUrlPreview: audioUrl ? audioUrl.substring(0, 100) : null,
      audioUrlLength: audioUrl?.length || 0,
      willShowAudio: !!audioUrl && !!assessmentResult,
      hasAssessmentResult: !!assessmentResult,
      questionNumber
    });
  }, [audioUrl, assessmentResult, questionNumber]);

  // Test: Add global click listener to detect if clicks are being captured
  useEffect(() => {
    const handleGlobalClick = (e) => {
      const target = e.target;
      if (target.closest('.pronunciation-play-button') || 
          target.closest('.word-play-button') || 
          target.closest('.audio-player')) {
        console.log('🌐 Global click detected on:', target.className, target.tagName);
      }
    };
    
    document.addEventListener('click', handleGlobalClick, true); // Use capture phase
    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
    };
  }, []);

  return (
    <div className="relative flex flex-col gap-5" style={{ pointerEvents: 'auto' }}>
      <ReviewQuestionHeader
        questionType="Pronunciation"
        currentQuestionNumber={reportProps?.currentQuestionNumber}
        totalQuestions={reportProps?.totalQuestions}
        score={reportProps?.score}
        reportSlot={reportProps ? (
          <ReportSoalButton
            type={reportProps.reportType}
            prosetId={reportProps.prosetId}
            soalId={reportProps.soalId}
            allowReReport={true}
            persistReportedState={true}
            reportText="Report this question"
          />
        ) : null}
      />

      <div className="relative flex flex-col gap-5" style={{ pointerEvents: 'auto' }}>
        {/* changed by Cursor: keep only one Text to Read section (the larger one), remove the smaller duplicate */}
        {textToRead && (
          <div>
            <h2 className="m-0 text-xl font-medium text-[var(--text-primary)] leading-relaxed">{textToRead}</h2>
            
            {translation && (
              <>
                <div className="h-px bg-[var(--border-color)] my-4" />
                <div>
                  <div className="text-sm font-semibold text-[var(--text-primary)] mb-2">Translation:</div>
                  <p className="m-0 text-base text-[var(--text-secondary)] italic">
                    {translation}
                  </p>
                </div>
              </>
            )}
          </div>
        )}
        
        {/* Show audio even if assessmentResult is not available */}
        {audioUrl && !assessmentResult && (
          <div className="relative mt-[60px] mb-0 flex flex-col gap-2" style={{ pointerEvents: 'auto' }}>
            <div className="relative z-10 bg-[linear-gradient(135deg,rgba(59,130,246,0.12)_0%,rgba(14,165,233,0.07)_100%)] dark:bg-[linear-gradient(135deg,rgba(59,130,246,0.12)_0%,rgba(37,99,235,0.06)_100%)] border-2 border-blue-400/30 dark:border-blue-400/35 rounded-2xl p-5" style={{ pointerEvents: 'auto' }}>
              <h3 className="m-0 mb-4 text-base font-semibold text-[var(--text-primary)]">Your Recording</h3>
              <QuestionAudioPlayer
                audioUrl={audioUrl}
                showQuota={false}
                maxPlaysMode="unlimited"
                fullWidth={true}
              />
            </div>
          </div>
        )}

        {assessmentResult ? (
          <div className="relative mt-[60px] mb-3 flex flex-col gap-4" style={{ pointerEvents: 'auto' }}>
            {/* Overall Score Card */}
            <OverallScoreCard score={overallScores['accuracy-score']} />

            {/* Audio Player - Your Recording */}
            {audioUrl ? (
              <div className="relative z-10 mt-5 bg-[linear-gradient(135deg,rgba(59,130,246,0.12)_0%,rgba(14,165,233,0.07)_100%)] dark:bg-[linear-gradient(135deg,rgba(59,130,246,0.12)_0%,rgba(37,99,235,0.06)_100%)] border-2 border-blue-400/30 dark:border-blue-400/35 rounded-2xl p-5" style={{ pointerEvents: 'auto' }}>
                <h3 className="m-0 mb-4 text-base font-semibold text-[var(--text-primary)]">Your Recording</h3>
                <QuestionAudioPlayer
                  audioUrl={audioUrl}
                  showQuota={false}
                  maxPlaysMode="unlimited"
                  fullWidth={true}
                />
              </div>
            ) : (
              <div className="bg-[linear-gradient(135deg,rgba(59,130,246,0.12)_0%,rgba(14,165,233,0.07)_100%)] dark:bg-[linear-gradient(135deg,rgba(59,130,246,0.12)_0%,rgba(37,99,235,0.06)_100%)] border-2 border-blue-400/30 dark:border-blue-400/35 rounded-2xl p-5">
                <h3 className="m-0 mb-4 text-base font-semibold text-[var(--text-primary)]">Your Recording</h3>
                <div className="py-2.5 text-center text-sm text-[var(--text-tertiary)]">
                  No audio recording available
                </div>
              </div>
            )}
          
            {/* Word Analysis - per word score + TTS */}
            {wordDetails && wordDetails.length > 0 ? (
              <div className="relative mb-0" style={{ pointerEvents: 'auto' }}>
                <h3 className="m-0 mb-3 text-base font-semibold text-[var(--text-primary)]">Word Analysis</h3>
                <div className="flex flex-col gap-3 relative" style={{ pointerEvents: 'auto' }}>
                  {wordDetails.map((wordDetail, index) => {
                    const score = wordDetail['accuracy-score'] !== undefined && wordDetail['accuracy-score'] !== null 
                      ? Math.floor(wordDetail['accuracy-score']) 
                      : 0;
                    const isGood = score > 74;
                    const errorType = wordDetail['error-type'] || 'Unknown';
                    
                    return (
                      <div key={index} className="relative z-10 flex items-center gap-4 p-4 bg-[var(--card-bg)] dark:bg-[var(--bg-tertiary)] rounded-xl border-2 border-transparent hover:border-teal-500/20 hover:shadow-md transition-all">
                        <button 
                          type="button"
                          className="word-play-button p-2 rounded-lg bg-gradient-to-br from-teal-600 to-teal-700 text-white cursor-pointer flex items-center justify-center transition-all hover:scale-105 hover:shadow-lg touch-manipulation relative z-[9999]"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.nativeEvent?.stopImmediatePropagation?.();
                            const word = wordDetail.word;
                            if (word && word.trim()) {
                              try {
                                speech.playWord(word, index);
                              } catch (error) {
                                console.error('❌ Error calling playWord:', error);
                              }
                            }
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.nativeEvent?.stopImmediatePropagation?.();
                          }}
                          onTouchStart={(e) => e.stopPropagation()}
                          aria-label={`Play word: ${wordDetail.word}`}
                          title={speech.playingWordIndex === index ? 'Stop audio' : 'Play audio'}
                        >
                          <SpeakerHigh 
                            size={20} 
                            weight={speech.playingWordIndex === index ? "fill" : "regular"} 
                          />
                        </button>
                        <span className="flex-1 text-lg font-semibold text-[var(--text-primary)]">{wordDetail.word}</span>
                        <span className={`text-sm font-semibold px-3 py-1.5 rounded-md min-w-[50px] text-center border-2 ${
                          isGood 
                            ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-400 border-emerald-500 dark:border-emerald-500/40' 
                            : 'bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-400 border-red-500 dark:border-red-500/40'
                        }`}>
                          {errorType === 'None' ? '✓' : '✗'} {score}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="mb-0">
                <h3 className="m-0 mb-3 text-base font-semibold text-[var(--text-primary)]">Word Analysis</h3>
                <div className="py-2.5 text-center text-sm text-[var(--text-tertiary)]">
                  No word analysis available
                </div>
              </div>
            )}
          </div>
        ) : null}

          {/* Explanation - di atas recap */}
          {explanation && (
            <div className="bg-[linear-gradient(135deg,#fff9e6_0%,#ffeaa7_100%)] dark:bg-[linear-gradient(135deg,#3d3520_0%,#4a3d1a_100%)] p-5 rounded-xl mb-6 border-l-4 border-l-[#ffd93d]">
              <h3 className="text-base font-bold text-amber-600 dark:text-amber-400 mb-3 flex items-center gap-2">Penjelasan</h3>
              <p className="text-[15px] text-[var(--text-primary)] leading-relaxed m-0">{explanation}</p>
            </div>
          )}

          <RecapSection recap={recap} annotations={annotations} question={question} />
      </div>
    </div>
  );
};

export default PronunciationReview;

